import {
  IAbstractMediaContent,
  IEvent,
  IImageMediaContent, IImageTransform, IMedia,
  IMultiMediaContentBlock,
  IVideoMediaContent
} from "@webng-types/write-model";
import {ImageMediaItemViewModel, MediaItemViewModel, VideoMediaItemViewModel} from "./ViewModel";
import {aspectWidthSizedImageUrl, exactSizeImageUrl, ReturnImage, thumbnailSource} from "./imageUrl";
import {effectiveSourceDimensions, type ImageTransform} from "@tickaroo/media-transform";


const VALID_ROTATIONS = new Set([0, 90, 180, 270])
const isFiniteNumber = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n)
const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

// Map the placement's flat sync fields (crop_x/crop_y/… on ImageMediaContent's
// transform) to the shared runtime transform. Returns undefined when nothing is
// set — so an un-cropped image renders exactly as before (no proxy filters).
//
// These values are interpolated verbatim into media-proxy path segments (a_/c_crop),
// so they are validated here rather than trusted from the write model: rotate must
// be one of the proxy's supported angles, and crop/focal must be finite and within
// 0..1. Every value the editor produces already satisfies this, so it is a no-op
// for valid content — it only drops/clamps malformed or out-of-range stored data.
export function toRuntimeImageTransform(t: IImageTransform|undefined): ImageTransform|undefined {
  if(!t) return undefined

  const rotate = isFiniteNumber(t.rotate) && VALID_ROTATIONS.has(t.rotate) ? t.rotate : 0

  let crop: ImageTransform["crop"]
  if(isFiniteNumber(t.crop_width) && isFiniteNumber(t.crop_height)) {
    const x = isFiniteNumber(t.crop_x) ? clamp01(t.crop_x) : 0
    const y = isFiniteNumber(t.crop_y) ? clamp01(t.crop_y) : 0
    // Clamp extents as a rectangle (against 1 - x / 1 - y), not each component
    // independently, so a malformed transform can't emit an out-of-bounds crop the
    // proxy rejects. A no-op for valid crops, where x + width <= 1 already holds.
    const width = Math.min(clamp01(t.crop_width), 1 - x)
    const height = Math.min(clamp01(t.crop_height), 1 - y)
    if(width > 0 && height > 0) {
      crop = {x, y, width, height}
    }
  }

  const focal: ImageTransform["focal"] = isFiniteNumber(t.focal_x) && isFiniteNumber(t.focal_y)
    ? {x: clamp01(t.focal_x), y: clamp01(t.focal_y)}
    : undefined

  if(rotate === 0 && !crop && !focal) return undefined
  return {
    rotate: rotate || undefined,
    crop,
    focal
  }
}

export function imageMediaSourceSetTargets(media: IMedia|undefined, transform?: ImageTransform) {
  const rawSource = thumbnailSource(media)
  // Size the srcset against the cropped-and-rotated dimensions so the candidate
  // widths and the rendered aspect ratio match what the proxy returns; the raw
  // url stays the source and `transform` becomes the a_/c_crop filters.
  const effective = effectiveSourceDimensions(rawSource.width, rawSource.height, transform)
  const tSource = {url: rawSource.url, width: effective.width, height: effective.height}

  const sourceWidth = tSource.width || 960;
  let targets = new Array<ReturnImage>();

  targets.push(aspectWidthSizedImageUrl(tSource, 320, {}, transform));

  for (const targetWidth of [420, 640, 960, 1250, 1600]) {
    if (sourceWidth > targetWidth * 1.1) { // only use a scaled version if picture is more than 10% bigger than the requested size, otherwise just use the original size
      targets.push(aspectWidthSizedImageUrl(tSource, targetWidth, {format: 'webp'}, transform));
    } else if(tSource.width && tSource.height) {
      targets.push(exactSizeImageUrl(tSource.url, {width: tSource.width, height: tSource.height, fit: "null", format: 'webp'}, transform));
      break;
    }
  }

  return {source: tSource, targets: targets}
}

function liveblogImageMediaItemConverter(event: IEvent,
                                         contentblock: IMultiMediaContentBlock,
                                         item: IImageMediaContent): ImageMediaItemViewModel {
  const media = event.media?.find(media => media.local_id === item.media_local_id);

  const transform = toRuntimeImageTransform(item.transform);
  const {source, targets} = imageMediaSourceSetTargets(media, transform);

  return {
    kind: "image",
    id: item.media_local_id!,
    title: item.title || "",
    credit: item.credit || "",
    contentWarning: item.content_warning,
    url: media?.url || "",
    originalWidth: source.width || 0,
    originalHeight: source.height || 0,
    previews: targets,
    media: media,
    linkUrl: item.link_url
  };
}

function liveblogVideoMediaItemConverter(event: IEvent,
                                         contentBlock: IMultiMediaContentBlock,
                                         item: IVideoMediaContent): VideoMediaItemViewModel {
  const media = event.media?.find(media => media.local_id === item.media_local_id);

  const tSource = thumbnailSource(media)

  return {
    kind: "video",
    id: item.media_local_id!,
    title: item.title || "",
    credit: item.credit || "",
    contentWarning: item.content_warning,
    originalWidth: tSource.width || 0,
    originalHeight: tSource.height || 0,
    duration: media?.duration_ms || 0,
    previews: [
      aspectWidthSizedImageUrl(tSource, 160, {format: 'webp'}),
      aspectWidthSizedImageUrl(tSource, 320, {format: 'webp'}),
      aspectWidthSizedImageUrl(tSource, 640, {format: 'webp'}),
      aspectWidthSizedImageUrl(tSource, 960, {format: 'webp'})
    ],
    media: media
  };
}

export function liveblogMediaItemConverter(event: IEvent,
                                           contentBlock: IMultiMediaContentBlock,
                                           item: IAbstractMediaContent): MediaItemViewModel {
  switch(item._type) {
    case "Tik::Model::Content::ImageMediaContent":
      return liveblogImageMediaItemConverter(event, contentBlock, item);
    case "Tik::Model::Content::VideoMediaContent":
      return liveblogVideoMediaItemConverter(event, contentBlock, item);
    default:
      throw new Error("Unknown content type: " + item._type)
  }
}
